import { Button, Card, message, Modal, Popconfirm, Spin } from "antd";
import { LoadingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { POST } from "../../../utils/apirequest";
import { API } from "../../../config/API";

interface Prop {
  open: boolean;
  onCancel: () => void;
  onSaveClick: (rectangles: any) => void;
  imageUrl: any;
  initialCoords?: any[];
}

// Anything thinner than this is a stray click, not an article.
const MIN_BOX = 0.005;

const clamp = (value: number) => Math.max(0, Math.min(1, value));

// A box dragged bottom-up or right-to-left comes out of the mouse handlers with
// a negative width/height. strokeRect() still draws it, so it looks fine here,
// but the reader apps map the box straight onto CSS percentages and a negative
// percentage is invalid - the overlay collapses and the article cannot be
// clicked. Store every box anchored to its top-left corner instead.
const normalizeRect = (rect: any) => {
  const startX = Number(rect?.startX);
  const startY = Number(rect?.startY);
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  if (![startX, startY, width, height].every(Number.isFinite)) return null;

  const left = clamp(Math.min(startX, startX + width));
  const top = clamp(Math.min(startY, startY + height));
  const right = clamp(Math.max(startX, startX + width));
  const bottom = clamp(Math.max(startY, startY + height));

  if (right - left < MIN_BOX || bottom - top < MIN_BOX) return null;

  return {
    ...rect,
    startX: left,
    startY: top,
    width: right - left,
    height: bottom - top,
  };
};

const normalizeRects = (rects: any) =>
  Array.isArray(rects) ? rects.map(normalizeRect).filter(Boolean) : [];

const CanvasModal = ({
  open,
  onCancel,
  imageUrl,
  onSaveClick,
  initialCoords,
}: Prop) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentRect, setCurrentRect] = useState({
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
  });
  const [rectangles, setRectangles] = useState<any>(
    normalizeRects(initialCoords)
  );
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        setImage(img);
        setLoading(false);
      };
    }
  }, [imageUrl]);

  useEffect(() => {
    if (image && canvasRef.current) {
      redrawCanvas();
    }
  }, [image, rectangles]);

  useEffect(() => {
    if (image && initialCoords) {
      setRectangles(normalizeRects(initialCoords));
    }
  }, [image, initialCoords]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;

    if (canvas && image) {
      const context: any = canvas.getContext("2d");
      context?.clearRect(0, 0, canvas.width, canvas.height);
      context?.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.setLineDash([]);
      context.strokeStyle = "red";
      context.lineWidth = 2;
      rectangles.forEach((rect: any) => {
        context.strokeRect(
          rect.startX * canvas.width,
          rect.startY * canvas.height,
          rect.width * canvas.width,
          rect.height * canvas.height
        );
      });
    }
  };

  const handleMouseDown = (e: any) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      setCurrentRect({
        startX: (e.clientX - rect.left) / rect.width,
        startY: (e.clientY - rect.top) / rect.height,
        width: 0,
        height: 0,
      });
      setIsDrawing(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawing) return;
    const canvas: any = canvasRef.current;
    const context: any = canvas?.getContext("2d");
    const rect = canvas?.getBoundingClientRect();
    if (rect) {
      const newRect = {
        startX: currentRect.startX,
        startY: currentRect.startY,
        width: (e.clientX - rect.left) / rect.width - currentRect.startX,
        height: (e.clientY - rect.top) / rect.height - currentRect.startY,
      };
      setCurrentRect(newRect);
      redrawCanvas();
      context.strokeStyle = "red";
      context.lineWidth = 1;
      context.setLineDash([]);
      context.strokeRect(
        newRect.startX * canvas.width,
        newRect.startY * canvas.height,
        newRect.width * canvas.width,
        newRect.height * canvas.height
      );
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    const rect = normalizeRect({ ...currentRect, source: "manual" });
    // A plain click or a hairline drag is not an article - throw it away and
    // repaint so the stray outline does not linger on the canvas.
    if (!rect) {
      redrawCanvas();
      return;
    }
    setRectangles((prevRectangles: any) => [...prevRectangles, rect]);
  };

  const handleDelete = (index: number) => {
    setRectangles((prevRectangles: any) => {
      const newRectangles = [...prevRectangles];
      newRectangles.splice(index, 1);
      return newRectangles;
    });
  };

  const showKeyMissingModal = () => {
    Modal.error({
      title: "Auto-detect is not configured",
      content:
        "The GEMINI_API_KEY environment variable is not set on the server, " +
        "so articles cannot be auto-detected. Please add GEMINI_API_KEY to " +
        "the CMS .env and restart the server, or mark the boxes manually for now.",
      okText: "Got it",
    });
  };

  const handleAutoDetect = async () => {
    if (!imageUrl) return;
    try {
      setDetecting(true);
      const res: any = await POST(API.EPAPER_DETECT_ARTICLES, { imageUrl });
      if (res?.status && Array.isArray(res?.coords)) {
        setRectangles(res.coords);
        if (res.coords.length === 0) {
          message.warning("No articles detected. Try drawing manually.");
        } else {
          message.success(`Detected ${res.coords.length} articles`);
        }
      } else if (res?.code === "GEMINI_API_KEY_MISSING") {
        showKeyMissingModal();
      } else {
        message.error(res?.message || "Detection failed");
      }
    } catch (err: any) {
      console.error("auto-detect error", err);
      const data = err?.response?.data;
      if (data?.code === "GEMINI_API_KEY_MISSING") {
        showKeyMissingModal();
      } else {
        message.error(data?.message || err?.message || "Detection failed");
      }
    } finally {
      setDetecting(false);
    }
  };

  const drawRectangleOnCanvas = (rect: any, canvas: HTMLCanvasElement) => {
    if (canvas && image) {
      const context: any = canvas.getContext("2d");
      context?.clearRect(0, 0, canvas.width, canvas.height);
      context?.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.setLineDash([]);
      context.strokeStyle = "red";
      context.lineWidth = 2;
      context.strokeRect(
        rect.startX * canvas.width,
        rect.startY * canvas.height,
        rect.width * canvas.width,
        rect.height * canvas.height
      );
    }
  };

  const autoDetectButton = (() => {
    const hasExisting = rectangles?.length > 0;
    const button = (
      <Button
        type="primary"
        icon={
          detecting ? (
            <Spin
              size="small"
              indicator={<LoadingOutlined style={{ color: "white" }} spin />}
            />
          ) : (
            <ThunderboltOutlined />
          )
        }
        disabled={loading || detecting || !imageUrl}
        loading={detecting}
        onClick={hasExisting ? undefined : handleAutoDetect}
      >
        {detecting ? "Detecting..." : "Auto-detect Articles"}
      </Button>
    );
    if (hasExisting && !detecting) {
      return (
        <Popconfirm
          title="Replace current boxes?"
          description="Auto-detect will replace all existing boxes on this page."
          okText="Replace"
          cancelText="Cancel"
          onConfirm={handleAutoDetect}
        >
          {button}
        </Popconfirm>
      );
    }
    return button;
  })();

  return (
    <Modal
      title={"Mark Pages"}
      open={open}
      centered
      onCancel={() => onCancel()}
      footer={
        <div>
          <Button
            danger
            size="large"
            style={{ marginRight: "10px" }}
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={() => onSaveClick(rectangles)}
          >
            Save
          </Button>
        </div>
      }
      width={"80%"}
    >
      <br />
      <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
        <div style={{ width: "70%" }}>
          {loading ? (
            "Loading..."
          ) : (
            <Card title={"Draw Boxes"} extra={autoDetectButton}>
              <canvas
                ref={canvasRef}
                width={800}
                height={1000}
                style={{ width: "100%", height: "auto", cursor: "crosshair" }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              />
            </Card>
          )}
        </div>
        <div
          style={{
            width: "30%",
            overflowY: "scroll",
            maxHeight: "100vh",
          }}
        >
          <Card title={"Crop"} extra={`Crops ${rectangles?.length} `}>
            {rectangles?.reverse()?.map((rect: any, index: number) => (
              <div className="Epaper-cropItem">
                <div>{index + 1} . </div>
                <div style={{ flex: 1 }}>
                  <canvas
                    width={"100%"}
                    height={150}
                    style={{ border: "1px solid black" }}
                    ref={(el) => {
                      if (el) drawRectangleOnCanvas(rect, el);
                    }}
                  />
                </div>

                <Button size="small" danger onClick={() => handleDelete(index)}>
                  Remove
                </Button>
              </div>
            ))}
          </Card>
        </div>
      </div>
      <hr />
    </Modal>
  );
};

export default CanvasModal;
